<script lang="ts">
    import {getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {TextValueEditorValidations} from '../text/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormGroup} from '../../forms/form-group';
    import {FormControlBuilder} from '../../forms/form-control';
    import {formControls} from '../../forms/use.form-controls';
    import {style} from 'svelte-forms';
    import {getContext, onDestroy} from 'svelte';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {validatorFactory} from '../text/utils';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import {TextWithMetadataValueEditorModelExtension} from './types';
    import {createInputDescription} from '../../description/utils';
    import {FormGroupBuilder} from '../../forms/form-group';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {required} from '../../forms/use.required';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import Description from '../../description/Description.svelte';
    import Selectbox from '../_shared/Selectbox.svelte';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import {cloneDeep} from 'lodash-es';
    import TextualEditorsContextMenuWrapper from '../_shared/TextualEditorsContextMenuWrapper.svelte';
    import type {
        TextWithMetadataValueEditorLocalizations,
        TextWithMetadataValueEditorModel,
        TextWithMetadataValueEditorOptions
    } from './types';

    export let model: TextWithMetadataValueEditorModel;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: TextWithMetadataValueEditorOptions;
    export let validations: TextValueEditorValidations;

    let selectedExtensionValue: string;
    let inputElement: HTMLInputElement;

    const logger = getLogger();
    const localize: EditorsLocalizationFunction<TextWithMetadataValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const allExtensions = Object.values(TextWithMetadataValueEditorModelExtension);
    const extensions = options?.excludedExtensions
        ? allExtensions.filter((e) => !options.excludedExtensions.includes(e))
        : allExtensions;
    const labelContext = getValueEditorLabelContext();

    model = normalizeModel(model);

    const formControlInput = FormControlBuilder.for(`${editorId}Input`, `${editorName}Input`, model?.row)
        .withValidators(validatorFactory(validations, 'text'))
        .withOptions(formControlOptions)
        .withFormatters([emptyAsNullFormatter(options)])
        .build(logger);

    const formControlExtension = FormControlBuilder.for(`${editorId}Extension`, `${editorName}Extension`, model?.extension)
        .build(logger);

    const formGroupBuilder = new FormGroupBuilder<TextWithMetadataValueEditorModel>().addModifiers([emptyAsNullParser(options)]);
    formGroupBuilder.registerFormControl(formControlInput);
    formGroupBuilder.registerFormControl(formControlExtension);

    const formControl = formGroupBuilder.build(editorId, editorName, new Map(
        [[`${editorName}Extension`, 'extension'], [`${editorName}Input`, 'row']]
    ));

    const subscriptionExtension = formControlExtension.getViewValue$().subscribe((value) => selectedExtensionValue = value);  // two-way data binding -> sending updated value up to a parent
    $: formControlExtension.setViewValue(selectedExtensionValue); // two-way data binding -> receiving new model value from parent

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(normalizeModel(model)); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    onDestroy(() => {
        subscription.unsubscribe();
        subscriptionExtension.unsubscribe();
    });

    function normalizeModel(modelToNorm: any): TextWithMetadataValueEditorModel {
        let modelToNormalize = cloneDeep(modelToNorm);

        if (!modelToNormalize) {
            modelToNormalize = {extension: TextWithMetadataValueEditorModelExtension.STARTS_WITH, row: ''};
        }

        if (typeof modelToNormalize !== 'object') {
            if (typeof modelToNormalize === 'string') {
                modelToNormalize = {
                    extension: TextWithMetadataValueEditorModelExtension.EQUALS,
                    row: modelToNormalize ?? ''
                };
            } else {
                modelToNormalize = {extension: TextWithMetadataValueEditorModelExtension.STARTS_WITH, row: ''};
            }

            return modelToNormalize;
        }

        if (!modelToNormalize.extension) {
            modelToNormalize.extension = TextWithMetadataValueEditorModelExtension.STARTS_WITH;
        }

        // if model.extension is not a member of SearchTextValueEditorModelExtension enum
        if (!extensions.includes(modelToNormalize.extension)) {
            modelToNormalize.extension = TextWithMetadataValueEditorModelExtension.STARTS_WITH;
        }

        if (typeof modelToNormalize.row !== 'string') {
            modelToNormalize.row = '';
        }

        return modelToNormalize;
    }

    export function getFormController(): FormGroup<TextWithMetadataValueEditorModel> {
        return formControl;
    }
</script>

<TextualEditorsContextMenuWrapper {editorId} {inputElement} let:specialSymbolsKeyboardAction>
    <div class="input-group input-group-{size}" role="group" aria-labelledby={labelContext?.labelId ?? null}>
        <div class="input-group-btn">
            <span id="{editorId}-metadata-select-label" class="sr-only">
                {localize('metadataSelectLabel')}
                {labelContext?.labelText ?? ''}.
                {localize('currentSelectValue')}:
                <!-- after this is text content of select (added by headless-ui) -->
            </span>

            <Selectbox selected={selectedExtensionValue}
                       disabled={isDisabled}
                       {size}
                       selectboxOptions={extensions}
                       ariaLabelledby="{editorId}-metadata-select-label"
                       on:change={(event) => {selectedExtensionValue = event.detail; formControl.setTouched()}}>
                <svelte:fragment slot="button">
                    {localize(selectedExtensionValue)}
                </svelte:fragment>
                <svelte:fragment slot="option" let:option>
                    {localize(option)}
                </svelte:fragment>
            </Selectbox>
        </div>

        <span id="{editorId}-main-input-label" class="sr-only">
            {localize('mainInputLabel')}
            {labelContext?.labelText ?? ''}.
        </span>

        <input class="form-control input-{size}"
               type="text"
               id={editorId}
               name={editorName}
               placeholder={placeholder}
               disabled={isDisabled}
               maxlength={validations.maxlength}
               minlength={validations.minlength}
               use:required={validations.required}
               use:ariaErrormessage={formControl}
               use:ariaInvalid={formControl}
               use:focusOnMount={isFocused}
               use:formControls={formControlInput}
               use:style={{field: formControlInput.getFieldStateAsStore()}}
               use:touched={{formControl:formControlInput}}
               use:ariaDescribedby={inputDescription}
               use:specialSymbolsKeyboardAction
               bind:this={inputElement}
               aria-labelledby="{editorId}-main-input-label"

               data-main-input/>
    </div>

    <!-- errors are taken directly from formControlInput because formControlExtension cannot have error and formControlInput
        don't have prefix in errors like formControl would have -->
    <ErrorMessages formController={formControl} errors={formControlInput.getErrors$()} {forceShowErrors} {size}/>
    <Description {inputDescription}/>
</TextualEditorsContextMenuWrapper>